import { getAPIHeaderOptions, transformErrorResponse } from "../../utils/apiUtils";
import {
	IAddCameraProfileRequestData,
	IAddCameraRequestData,
	ICameraControlsData,
	ICameraDetails,
	IChangeUserPasswordRequestData,
	ICreateUserRequestData,
	IDeleteCameraProfileRequestData,
	IEditBuildingConfigurationRequestData,
	IEditUserRequestData,
	IGetDeviceTimeResponseData,
	IGetRTSPImageResponseData,
	IGetUserActivityLogsRequestData,
	IGetUserActivityLogsResponseData,
	IGetUsersListRequestData,
	IGetUsersListResponseData,
	INTPStatusDetails,
	IResetCameraControlsRequestData,
	ISyncDeviceTimeRequestData,
	IUpdateBarrierSettingsRequestData,
	IUpdateCameraDetailsRequestData,
	IUpdateCameraProfilesRequestData
} from "../../interfaces";
import { queries } from "./queries.reducer";

const configuration = queries.injectEndpoints({
	endpoints: (builder) => ({
		// REBOOT SYSTEM
		rebootSystem: builder.mutation<void, void>({
			query: () => ({
				url: "/settings/reboot_device",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// PING PONG
		pingPong: builder.query<"success" | "failed", void | null>({
			query: () => ({
				url: "/auth/pingpong",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformResponse: (_response, meta) => {
				if (meta && meta.response && meta.response.status === 200) {
					return "success";
				}
				return "failed";
			}
		}),

		// GET USERS LIST
		getUsersList: builder.query<IGetUsersListResponseData, IGetUsersListRequestData>({
			query: (requestData) => ({
				url: "/auth/users/data",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// CREATE USER
		createUser: builder.mutation<void, ICreateUserRequestData>({
			query: (requestData) => ({
				url: "/auth/signup",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// EDIT USER
		editUser: builder.mutation<void, IEditUserRequestData>({
			query: (requestData) => ({
				url: `/auth/users/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// DELETE USER
		deleteUser: builder.mutation<void, number>({
			query: (id) => ({
				url: `/auth/users/${id}`,
				headers: getAPIHeaderOptions(true),
				method: "DELETE"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// RESET SYSTEM
		resetSystem: builder.mutation<void, string>({
			query: (token) => ({
				url: "/settings/flush_db",
				headers: { ...getAPIHeaderOptions(), Authorization: `token ${token}` },
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET DEVICE TIME
		getDeviceTime: builder.query<IGetDeviceTimeResponseData, void>({
			query: () => ({
				url: "/settings/device_time_sync",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformResponse: (response: { data: IGetDeviceTimeResponseData }) => response.data
		}),

		// SYNC DEVICE TIME
		syncDeviceTime: builder.mutation<void, ISyncDeviceTimeRequestData>({
			query: (requestData) => ({
				url: "/settings/device_time_sync",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// UPDATE BARRIER SETTINGS
		updateBarrierSettings: builder.mutation<void, IUpdateBarrierSettingsRequestData>({
			query: (requestData) => ({
				url: "/settings/config/1",
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// UPDATE CAMERA DETAILS
		updateCameraDetails: builder.mutation<ICameraDetails, IUpdateCameraDetailsRequestData>({
			query: (requestData) => ({
				url: `/cameraconfig/camera/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// DELETE CAMERA
		deleteCamera: builder.mutation<void, number>({
			query: (id) => ({
				url: `/cameraconfig/camera/${id}`,
				headers: getAPIHeaderOptions(true),
				method: "DELETE"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// ADD CAMERA
		addCamera: builder.mutation<void, IAddCameraRequestData>({
			query: (requestData) => ({
				url: "/cameraconfig/camera",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// EDIT BUILDING CONFIGURATION
		editBuildingConfiguration: builder.mutation<unknown, IEditBuildingConfigurationRequestData>({
			query: (requestData) => ({
				url: `/analytics/society/${requestData.id}`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// CHANGE USER PASSWORD
		changeUserPassword: builder.mutation<unknown, IChangeUserPasswordRequestData>({
			query: (requestData) => ({
				url: "/auth/password/update",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// REBOOT ML ENGINE
		rebootInference: builder.mutation<void, null>({
			query: () => ({
				url: "/settings/restart_ml_engine",
				headers: getAPIHeaderOptions(true),
				method: "GET"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// GET USER ACTIVITY LOGS
		getUserActivityLogs: builder.query<IGetUserActivityLogsResponseData, IGetUserActivityLogsRequestData>({
			query: (requestData) => ({
				url: "/analytics/user-activity-log",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: requestData
			})
		}),

		// GET RTSP IMAGE
		getRTSPImage: builder.query<IGetRTSPImageResponseData, string>({
			query: (rtspUri) => ({
				url: "/cameraconfig/get_image",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: { rtsp_uri: rtspUri }
			}),
			transformErrorResponse: (response) => {
				if (response.data) {
					const responseDetails = response.data as { status: number; detail: string; error: string };
					response.data = responseDetails.detail;
				}

				return response;
			}
		}),

		// GET CAMERA CONTROLS
		// getCameraControls: builder.query<ICameraControlsData[], number>({
		// 	query: (cameraId) => ({
		// 		url: `/cameraconfig/econ_cameras/${cameraId}/controls`,
		// 		headers: getAPIHeaderOptions(true),
		// 		method: "GET"
		// 	})
		// }),

		// UPDATE CAMERA CONTROLS
		// updateCameraControls: builder.mutation<unknown, IUpdateCameraControlsRequestData>({
		// 	query: (requestData) => ({
		// 		url: `/cameraconfig/econ_cameras/${requestData.id}/set_control`,
		// 		headers: getAPIHeaderOptions(true),
		// 		method: "POST",
		// 		body: requestData.data
		// 	}),
		// 	transformErrorResponse: transformErrorResponse
		// }),

		// RESET CAMERA CONTROLS
		// resetCameraControls: builder.mutation<unknown, IResetCameraControlsRequestData>({
		// 	query: (requestData) => ({
		// 		url: `/cameraconfig/econ_cameras/${requestData.id}/reset_controls`,
		// 		headers: getAPIHeaderOptions(true),
		// 		method: "POST",
		// 		body: requestData.data
		// 	}),
		// 	transformErrorResponse: transformErrorResponse
		// }),

		// GET CAMERA PROFILES
		getCameraProfiles: builder.query<ICameraControlsData[], number>({
			query: (cameraId) => ({
				url: `/cameraconfig/camera/${cameraId}/profiles`,
				headers: getAPIHeaderOptions(true),
				method: "GET"
			})
		}),

		// UPDATE CAMERA PROFILES
		updateCameraProfiles: builder.mutation<unknown, IUpdateCameraProfilesRequestData>({
			query: ({ camera_id, profiles }) => ({
				url: `/cameraconfig/camera/${camera_id}/profiles/batch`,
				headers: getAPIHeaderOptions(true),
				method: "PATCH",
				body: { profiles }
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// ADD CAMERA PROFILE
		addCameraProfile: builder.mutation<ICameraControlsData, IAddCameraProfileRequestData>({
			query: (requestData) => ({
				url: `/cameraconfig/camera/${requestData.camera_id}/profiles`,
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: requestData.data
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// DELETE CAMERA PROFILE
		deleteCameraProfile: builder.mutation<unknown, IDeleteCameraProfileRequestData>({
			query: (requestData) => ({
				url: `/cameraconfig/camera/${requestData.camera_id}/profiles/${requestData.profile_id}`,
				headers: getAPIHeaderOptions(true),
				method: "DELETE"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// RESET CAMERA PROFILE
		resetCameraProfile: builder.mutation<unknown, IResetCameraControlsRequestData>({
			query: (requestData) => ({
				url: `/cameraconfig/camera/${requestData.camera_id}/profiles/${requestData.profile_id}/reset_controls`,
				headers: getAPIHeaderOptions(true),
				method: "POST"
			}),
			transformErrorResponse: transformErrorResponse
		}),

		// SET NTP SERVER
		setNTPServer: builder.mutation<unknown, string>({
			query: (ntp_ip) => ({
				url: "/settings/ntp/set",
				headers: getAPIHeaderOptions(true),
				method: "POST",
				body: { ntp_ip }
			}),
			transformErrorResponse
		}),

		// RESET NTP
		resetNTP: builder.mutation<unknown, void>({
			query: () => ({
				url: "/settings/ntp/reset",
				headers: getAPIHeaderOptions(true),
				method: "POST"
			}),
			transformErrorResponse
		}),

		// GET NTP STATUS
		getNTPStatus: builder.query<INTPStatusDetails, void>({
			query: () => ({
				url: "/settings/ntp/status",
				headers: getAPIHeaderOptions(true),
				method: "GET",
				params: { reveal_ip: true }
			})
		})
	})
});

export const {
	// REBOOT SYSTEM
	useRebootSystemMutation,
	// PING PONG
	useLazyPingPongQuery,
	// GET USERS LIST
	useGetUsersListQuery,
	// CREATE USER
	useCreateUserMutation,
	// EDIT USER
	useEditUserMutation,
	// DELETE USER
	useDeleteUserMutation,
	// RESET SYSTEM
	useResetSystemMutation,
	// GET DEVICE TIME
	useGetDeviceTimeQuery,
	// SYNC DEVICE TIME
	useSyncDeviceTimeMutation,
	// UPDATE BARRIER SETTINGS
	useUpdateBarrierSettingsMutation,
	// UPDATE CAMERA DETAILS
	useUpdateCameraDetailsMutation,
	// DELETE CAMERA
	useDeleteCameraMutation,
	// ADD CAMERA
	useAddCameraMutation,
	// EDIT BUILDING CONFIGURATION
	useEditBuildingConfigurationMutation,
	// CHANGE USER PASSWORD
	useChangeUserPasswordMutation,
	// REBOOT ML ENGINE
	useRebootInferenceMutation,
	// GET USER ACTIVITY LOGS
	useGetUserActivityLogsQuery,
	// GET RTSP IMAGE
	useGetRTSPImageQuery,
	// GET CAMERA CONTROLS
	// useGetCameraControlsQuery,
	// UPDATE CAMERA CONTROLS
	// useUpdateCameraControlsMutation,
	// RESET CAMERA CONTROLS
	// useResetCameraControlsMutation,
	// GET CAMERA PROFILES
	useGetCameraProfilesQuery,
	// UPDATE CAMERA PROFILES
	useUpdateCameraProfilesMutation,
	// ADD CAMERA PROFILE
	useAddCameraProfileMutation,
	// DELETE CAMERA PROFILE
	useDeleteCameraProfileMutation,
	// RESET CAMERA PROFILE
	useResetCameraProfileMutation,
	// SET NTP SERVER
	useSetNTPServerMutation,
	// RESET NTP
	useResetNTPMutation,
	// GET NTP STATUS
	useGetNTPStatusQuery
} = configuration;

export default configuration;
